/** SUNTRONIX ONE — Monthly ITAM Service Billing · Phase 1.
 *
 *  Single surface for finance operators to open a period, sweep
 *  billable events into per-customer monthly statements, share them
 *  with the customer, absorb approvals / queries, and convert
 *  approved statements into invoices — feeding straight into the
 *  existing Invoice Centre + ageing / monthly-pack ZIP flows.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  CalendarClock, RefreshCcw, Play, Send, CheckCircle2,
  AlertTriangle, FileText, MessageSquareWarning, Receipt, Plus, Lock, X,
} from "lucide-react";

const STMT_TONE = {
  DRAFT:    "bg-slate-100 text-slate-700",
  SHARED:   "bg-sky-100 text-sky-800",
  APPROVED: "bg-emerald-100 text-emerald-800",
  QUERIED:  "bg-amber-100 text-amber-900",
  INVOICED: "bg-indigo-100 text-indigo-800",
  VOID:     "bg-slate-200 text-slate-500 line-through",
};

const PERIOD_TONE = {
  OPEN:   "bg-emerald-100 text-emerald-800",
  LOCKED: "bg-amber-100 text-amber-900",
  CLOSED: "bg-slate-200 text-slate-600",
};

const TABS = [
  { id: "overview",   label: "Overview" },
  { id: "statements", label: "Statements" },
  { id: "queries",    label: "Queries" },
  { id: "invoices",   label: "Invoices" },
  { id: "payments",   label: "Payments" },
];

const fmt = (n, ccy = "INR") =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: ccy || "INR", maximumFractionDigits: 2 }).format(n || 0);


export default function BillingCentre() {
  const { user } = useAuth();
  const isSuntronix = !user?.master_company_id;
  const [tab, setTab] = useState("overview");
  const [dashboard, setDashboard] = useState(null);
  const [statements, setStatements] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showOpen, setShowOpen] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [selectedStatementId, setSelectedStatementId] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [dash, stmts] = await Promise.all([
        api.get("/billing/dashboard"),
        api.get("/billing/statements"),
      ]);
      setDashboard(dash.data);
      setStatements(stmts.data.items || []);
      setPeriods(dash.data.periods || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const tiles = useMemo(() => {
    const c = dashboard?.statement_counts || {};
    return [
      { key: "DRAFT",    label: "Draft",    n: c.DRAFT || 0,    tone: "text-slate-700" },
      { key: "SHARED",   label: "Shared",   n: c.SHARED || 0,   tone: "text-sky-800" },
      { key: "APPROVED", label: "Approved", n: c.APPROVED || 0, tone: "text-emerald-700" },
      { key: "QUERIED",  label: "Queried",  n: c.QUERIED || 0,  tone: "text-amber-800" },
      { key: "INVOICED", label: "Invoiced", n: c.INVOICED || 0, tone: "text-indigo-700" },
    ];
  }, [dashboard]);

  const queried = useMemo(
    () => statements.filter((s) => s.status === "QUERIED"),
    [statements]
  );

  const filteredStatements = useMemo(() => {
    if (tab === "invoices")  return statements.filter((s) => s.status === "INVOICED");
    if (tab === "queries")   return queried;
    return statements;
  }, [statements, queried, tab]);

  return (
    <div className="space-y-6 max-w-[1400px]" data-testid="billing-centre">
      <PageHeader
        eyebrow="Commercial · Monthly ITAM Billing"
        title="Monthly ITAM Service Billing"
        context="Open a period, sweep completed billable events into per-customer monthly statements, absorb the customer's approval or query, and generate an invoice — all without leaving this surface."
        actions={
          <div className="flex items-center gap-2">
            {isSuntronix && (
              <>
                <Button variant="outline" onClick={() => setShowOpen(true)}
                        data-testid="billing-open-period-btn">
                  <Plus className="h-3.5 w-3.5 mr-1.5" /> Open period
                </Button>
                <Button onClick={() => setShowGenerate(true)}
                        data-testid="billing-generate-btn">
                  <Play className="h-3.5 w-3.5 mr-1.5" /> Generate statements
                </Button>
              </>
            )}
            <Button variant="outline" onClick={load} disabled={busy}
                    data-testid="billing-refresh-btn">
              <RefreshCcw className={`h-3.5 w-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        }
      />

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {tiles.map((t) => (
          <button key={t.key}
            onClick={() => setTab("statements")}
            className="st-panel p-3 text-left hover:bg-slate-50 transition-colors"
            data-testid={`billing-tile-${t.key.toLowerCase()}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{t.label}</div>
            <div className={`text-2xl font-heading font-semibold tabular-nums ${t.tone}`}>{t.n}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">
              {fmt(dashboard?.amount_by_status?.[t.key] || 0)}
            </div>
          </button>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-3 py-2 text-[12px] font-medium tracking-wide border-b-2 -mb-px transition-colors
              ${tab === t.id
                ? "border-copper-600 text-copper-700"
                : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid={`billing-tab-${t.id}`}>
            {t.label}
            {t.id === "queries" && queried.length > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-100 text-amber-900 text-[10px] font-semibold">
                {queried.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <OverviewTab periods={periods} statements={statements}
                     isSuntronix={isSuntronix}
                     onSelectStatement={setSelectedStatementId}
                     onReload={load} />
      )}
      {(tab === "statements" || tab === "invoices" || tab === "queries") && (
        <StatementsTable rows={filteredStatements}
                         onSelect={setSelectedStatementId}
                         emptyText={
                           tab === "invoices" ? "No statements have been invoiced yet." :
                           tab === "queries"  ? "No open customer queries — nothing to chase." :
                           "No statements yet — generate the first one from an open period."} />
      )}
      {tab === "payments" && (
        <PaymentsTab statements={statements.filter((s) => s.status === "INVOICED")} />
      )}

      {showOpen && (
        <OpenPeriodDialog onClose={() => setShowOpen(false)}
                          onOpened={() => { setShowOpen(false); load(); }} />
      )}
      {showGenerate && (
        <GenerateStatementsDialog periods={periods}
          onClose={() => setShowGenerate(false)}
          onGenerated={() => { setShowGenerate(false); load(); }} />
      )}
      {selectedStatementId && (
        <StatementDetailDialog statementId={selectedStatementId}
                               isSuntronix={isSuntronix}
                               onClose={() => setSelectedStatementId(null)}
                               onChanged={load} />
      )}
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/*  Overview                                                                  */
/* -------------------------------------------------------------------------- */
function OverviewTab({ periods, statements, isSuntronix, onSelectStatement, onReload }) {
  const recent = statements.slice(0, 8);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {isSuntronix && (
        <Section title="Billing periods" hint="Latest 12 months" testId="billing-periods-section">
          <div className="st-panel divide-y divide-slate-100">
            {periods.length === 0 && (
              <div className="p-6 text-center text-[12px] text-slate-400">
                No periods yet — open your first one to start accruing statements.
              </div>
            )}
            {periods.map((p) => (
              <PeriodRow key={p.id} p={p} onChanged={onReload} />
            ))}
          </div>
        </Section>
      )}
      <div className={isSuntronix ? "lg:col-span-2" : "lg:col-span-3"}>
        <Section title="Recent statements" testId="billing-recent-statements">
          <div className="st-panel overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Statement</th>
                  <th className="text-left px-3 py-2">Customer</th>
                  <th className="text-left px-3 py-2">Period</th>
                  <th className="text-right px-3 py-2">Amount</th>
                  <th className="text-left px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((s) => (
                  <tr key={s.id} onClick={() => onSelectStatement(s.id)}
                      className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                      data-testid={`billing-recent-row-${s.id}`}>
                    <td className="px-3 py-2 font-mono text-[12px]">{s.statement_number}</td>
                    <td className="px-3 py-2">{s.customer_name || s.master_company_id}</td>
                    <td className="px-3 py-2 text-[11px] text-slate-500">{s.period_key}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(s.grand_total, s.currency)}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wider ${STMT_TONE[s.status]}`}>
                        {s.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {recent.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-8 text-[12px] text-slate-400">
                    No statements yet.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Section>
      </div>
    </div>
  );
}

function PeriodRow({ p, onChanged }) {
  const [busy, setBusy] = useState(false);
  const advance = async (endpoint) => {
    if (!window.confirm(`Proceed to ${endpoint}?`)) return;
    setBusy(true);
    try {
      await api.post(`/billing/periods/${p.id}/${endpoint}`);
      toast.success(`Period ${endpoint}ed`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refused");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3 p-3" data-testid={`billing-period-${p.id}`}>
      <CalendarClock className="h-4 w-4 text-slate-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-mono text-[13px]">{p.period_key}</div>
        <div className="text-[10px] text-slate-400">
          {p.start_date} → {p.end_date} · {p.currency}
        </div>
      </div>
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wider ${PERIOD_TONE[p.status]}`}>
        {p.status}
      </span>
      {p.status === "OPEN" && (
        <Button size="sm" variant="outline" onClick={() => advance("lock")}
                disabled={busy} data-testid={`billing-period-lock-${p.id}`}>
          <Lock className="h-3 w-3 mr-1" /> Lock
        </Button>
      )}
      {p.status === "LOCKED" && (
        <Button size="sm" variant="outline" onClick={() => advance("close")}
                disabled={busy} data-testid={`billing-period-close-${p.id}`}>
          <X className="h-3 w-3 mr-1" /> Close
        </Button>
      )}
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/*  Statements table                                                          */
/* -------------------------------------------------------------------------- */
function StatementsTable({ rows, onSelect, emptyText }) {
  return (
    <Section testId="billing-statements-section">
      <div className="st-panel overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Statement #</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Period</th>
              <th className="text-right px-3 py-2">Events</th>
              <th className="text-right px-3 py-2">Subtotal</th>
              <th className="text-right px-3 py-2">Tax</th>
              <th className="text-right px-3 py-2">Grand total</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Invoice</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} onClick={() => onSelect(s.id)}
                  className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  data-testid={`billing-stmt-row-${s.id}`}>
                <td className="px-3 py-2 font-mono text-[12px]">{s.statement_number}</td>
                <td className="px-3 py-2">{s.customer_name || s.master_company_id}</td>
                <td className="px-3 py-2 text-[11px] text-slate-500">{s.period_key}</td>
                <td className="px-3 py-2 text-right tabular-nums">{s.event_count}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmt(s.subtotal, s.currency)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmt(s.tax_total, s.currency)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmt(s.grand_total, s.currency)}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wider ${STMT_TONE[s.status]}`}>
                    {s.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-[11px] font-mono text-slate-500">
                  {s.invoice_number || "—"}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={9} className="text-center py-8 text-[12px] text-slate-400">
                {emptyText}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Section>
  );
}


/* -------------------------------------------------------------------------- */
/*  Payments (Phase 1 slim: shows invoices raised from statements)            */
/* -------------------------------------------------------------------------- */
function PaymentsTab({ statements }) {
  return (
    <Section title="Statement-linked invoices"
             hint="Once an approved statement is invoiced, payment lifecycle continues in the Invoice Centre."
             testId="billing-payments-section">
      <div className="st-panel overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Statement</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Invoice #</th>
              <th className="text-right px-3 py-2">Amount</th>
              <th className="text-left px-3 py-2">Invoiced at</th>
              <th className="text-left px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {statements.map((s) => (
              <tr key={s.id} className="border-t border-slate-100"
                  data-testid={`billing-invoiced-${s.id}`}>
                <td className="px-3 py-2 font-mono text-[12px]">{s.statement_number}</td>
                <td className="px-3 py-2">{s.customer_name || s.master_company_id}</td>
                <td className="px-3 py-2 font-mono text-[12px]">{s.invoice_number || "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmt(s.grand_total, s.currency)}</td>
                <td className="px-3 py-2 text-[11px] text-slate-500">
                  {s.invoiced_at ? new Date(s.invoiced_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-3 py-2 text-right">
                  {s.invoice_id && (
                    <a href={`/invoices?open=${s.invoice_id}`}
                       className="text-copper-700 hover:underline text-[11px]"
                       data-testid={`billing-open-invoice-${s.invoice_id}`}>
                      Open invoice →
                    </a>
                  )}
                </td>
              </tr>
            ))}
            {statements.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-[12px] text-slate-400">
                No invoices generated from statements yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Section>
  );
}


/* -------------------------------------------------------------------------- */
/*  Open-period dialog                                                        */
/* -------------------------------------------------------------------------- */
function OpenPeriodDialog({ onClose, onOpened }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [currency, setCurrency] = useState("INR");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/periods/open",
                                       { year: Number(year), month: Number(month),
                                         currency, notes: notes || undefined });
      toast.success(data.reused
        ? `Reusing period ${data.period.period_key}`
        : `Period ${data.period.period_key} opened`);
      onOpened?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Open failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="billing-open-period-dialog">
        <DialogHeader>
          <DialogTitle>Open billing period</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-[13px]">
          <label className="col-span-1">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Year</div>
            <Input type="number" value={year} onChange={(e) => setYear(e.target.value)}
                   data-testid="billing-period-year" />
          </label>
          <label className="col-span-1">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Month</div>
            <select className="w-full border rounded h-9 px-2"
                    value={month} onChange={(e) => setMonth(e.target.value)}
                    data-testid="billing-period-month">
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString("en", { month: "long" })}</option>
              ))}
            </select>
          </label>
          <label className="col-span-1">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Currency</div>
            <Input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                   data-testid="billing-period-currency" />
          </label>
          <label className="col-span-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Notes</div>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                   placeholder="Optional context — internal only"
                   data-testid="billing-period-notes" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="billing-open-period-submit">
            Open period
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* -------------------------------------------------------------------------- */
/*  Generate-statements dialog                                                */
/* -------------------------------------------------------------------------- */
function GenerateStatementsDialog({ periods, onClose, onGenerated }) {
  const [periodId, setPeriodId] = useState(periods[0]?.id || "");
  const [dryRun, setDryRun] = useState(true);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const run = async (asDryRun) => {
    if (!periodId) { toast.error("Select a period"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/billing/statements/generate",
                                       { period_id: periodId, dry_run: asDryRun });
      setPreview(data);
      if (!asDryRun) {
        toast.success(`Generated ${data.results.filter((r) => !r.skipped).length} statement(s)`);
        onGenerated?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sweep failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="billing-generate-dialog">
        <DialogHeader>
          <DialogTitle>Generate monthly statements</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-[13px]">
          <label>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Billing period</div>
            <select className="w-full border rounded h-9 px-2"
                    value={periodId} onChange={(e) => setPeriodId(e.target.value)}
                    data-testid="billing-generate-period">
              {periods.length === 0 && <option value="">— open a period first —</option>}
              {periods.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.period_key} · {p.status} · {p.currency}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={dryRun}
                   onChange={(e) => setDryRun(e.target.checked)}
                   data-testid="billing-generate-dry-run" />
            <span className="text-[12px]">Dry run — preview totals without writing statements</span>
          </label>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => run(true)} disabled={busy}
                    data-testid="billing-generate-preview-btn">
              Preview sweep
            </Button>
            <Button onClick={() => run(false)} disabled={busy}
                    data-testid="billing-generate-run-btn">
              <Play className="h-3.5 w-3.5 mr-1.5" /> Generate now
            </Button>
          </div>
          {preview && (
            <div className="st-panel p-3 space-y-2" data-testid="billing-generate-preview">
              <div className="text-[11px] text-slate-500">
                {preview.event_total} completed billable event(s) considered · {preview.results.length} customer(s)
              </div>
              <table className="w-full text-[12px]">
                <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left py-1">Customer</th>
                    <th className="text-right py-1">Events</th>
                    <th className="text-right py-1">Subtotal</th>
                    <th className="text-right py-1">Grand total</th>
                    <th className="text-left py-1">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.results.map((r, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="py-1 font-mono text-[11px]">{r.master_company_id}</td>
                      <td className="py-1 text-right tabular-nums">{r.event_count || 0}</td>
                      <td className="py-1 text-right tabular-nums">{fmt(r.subtotal, r.currency)}</td>
                      <td className="py-1 text-right tabular-nums font-semibold">{fmt(r.grand_total, r.currency)}</td>
                      <td className="py-1 text-[11px] text-slate-500">
                        {r.skipped ? `skipped — ${r.reason}` : (r.statement_number || "would generate")}
                      </td>
                    </tr>
                  ))}
                  {preview.results.length === 0 && (
                    <tr><td colSpan={5} className="text-center py-4 text-slate-400">
                      Nothing to sweep — no unbilled completed events in this period.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* -------------------------------------------------------------------------- */
/*  Statement detail                                                          */
/* -------------------------------------------------------------------------- */
function StatementDetailDialog({ statementId, isSuntronix, onClose, onChanged }) {
  const [stmt, setStmt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showApprove, setShowApprove] = useState(false);
  const [showQuery, setShowQuery] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/billing/statements/${statementId}`);
      setStmt(data.item);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
      onClose();
    }
  }, [statementId, onClose]);
  useEffect(() => { load(); }, [load]);

  const share = async () => {
    if (!window.confirm(`Share ${stmt.statement_number} with ${stmt.customer_name}?`)) return;
    setBusy(true);
    try {
      await api.post(`/billing/statements/${statementId}/share`, {});
      toast.success("Statement shared with customer");
      load(); onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Share failed");
    } finally { setBusy(false); }
  };

  const generateInvoice = async () => {
    if (!window.confirm(`Convert ${stmt.statement_number} into a draft invoice?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/billing/statements/${statementId}/generate-invoice`,
        { due_days: 30 });
      toast.success(`Invoice ${data.invoice.invoice_number} drafted`);
      load(); onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invoice failed");
    } finally { setBusy(false); }
  };

  if (!stmt) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-3xl">
          <div className="py-10 text-center text-[12px] text-slate-400">Loading…</div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto"
                     data-testid="billing-stmt-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span className="font-mono">{stmt.statement_number}</span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wider ${STMT_TONE[stmt.status]}`}>
              {stmt.status}
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[13px] mb-3">
          <FieldTile label="Customer" value={stmt.customer_name || stmt.master_company_id} />
          <FieldTile label="Period"   value={stmt.period_key} />
          <FieldTile label="Currency" value={stmt.currency} />
          <FieldTile label="Events"   value={stmt.event_count} />
          <FieldTile label="Subtotal" value={fmt(stmt.subtotal, stmt.currency)} />
          <FieldTile label="Tax"      value={fmt(stmt.tax_total, stmt.currency)} />
          <FieldTile label="Grand total" value={fmt(stmt.grand_total, stmt.currency)}
                     bold />
          <FieldTile label="Invoice" value={stmt.invoice_number || "—"} />
        </div>

        {/* Action bar */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {isSuntronix && (stmt.status === "DRAFT" || stmt.status === "QUERIED") && (
            <Button size="sm" onClick={share} disabled={busy}
                    data-testid="billing-stmt-share-btn">
              <Send className="h-3 w-3 mr-1" /> Share with customer
            </Button>
          )}
          {!isSuntronix && (stmt.status === "SHARED" || stmt.status === "QUERIED") && (
            <>
              <Button size="sm" onClick={() => setShowApprove(true)} disabled={busy}
                      data-testid="billing-stmt-approve-btn">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Approve
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowQuery(true)} disabled={busy}
                      data-testid="billing-stmt-query-btn">
                <MessageSquareWarning className="h-3 w-3 mr-1" /> Raise query
              </Button>
            </>
          )}
          {isSuntronix && stmt.status === "APPROVED" && (
            <Button size="sm" onClick={generateInvoice} disabled={busy}
                    data-testid="billing-stmt-invoice-btn">
              <Receipt className="h-3 w-3 mr-1" /> Generate invoice
            </Button>
          )}
          {stmt.invoice_id && (
            <a href={`/invoices?open=${stmt.invoice_id}`}
               className="text-copper-700 hover:underline text-[11px] ml-2"
               data-testid="billing-stmt-open-invoice">
              <FileText className="h-3 w-3 inline mr-1" />
              Open {stmt.invoice_number}
            </a>
          )}
        </div>

        {/* Queries */}
        {(stmt.queries || []).length > 0 && (
          <Section title="Customer queries" testId="billing-stmt-queries">
            <div className="space-y-2">
              {stmt.queries.map((q) => (
                <div key={q.id} className={`st-panel p-3 text-[12px] ${q.resolved_at ? "opacity-70" : ""}`}
                     data-testid={`billing-query-${q.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-medium">{q.raised_by_name || q.raised_by}</div>
                    <div className="text-[10px] text-slate-400">
                      {new Date(q.raised_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="text-slate-700">{q.reason}</div>
                  {q.disputed_event_ids?.length > 0 && (
                    <div className="text-[10px] text-slate-500 mt-1">
                      Disputed events: {q.disputed_event_ids.join(", ")}
                    </div>
                  )}
                  {q.resolved_at && (
                    <div className="mt-2 pt-2 border-t border-slate-100 text-[11px] text-emerald-700">
                      <CheckCircle2 className="h-3 w-3 inline mr-1" />
                      Resolved · {q.resolution || "no note"}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Line items */}
        <Section title={`Line events (${stmt.line_events?.length || 0})`} testId="billing-stmt-lines">
          <div className="st-panel overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Service</th>
                  <th className="text-left px-3 py-2">Description</th>
                  <th className="text-right px-3 py-2">Qty</th>
                  <th className="text-right px-3 py-2">Unit</th>
                  <th className="text-right px-3 py-2">Net</th>
                  <th className="text-right px-3 py-2">Tax</th>
                  <th className="text-right px-3 py-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {(stmt.line_events || []).map((ln, i) => (
                  <tr key={ln.event_id || i} className="border-t border-slate-100">
                    <td className="px-3 py-1.5 text-[11px] text-slate-500">{ln.event_date}</td>
                    <td className="px-3 py-1.5 font-mono text-[11px]">{ln.service_code}</td>
                    <td className="px-3 py-1.5">{ln.description}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{ln.quantity}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{fmt(ln.unit_price, stmt.currency)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{fmt(ln.net, stmt.currency)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{fmt(ln.tax, stmt.currency)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{fmt(ln.total, stmt.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        {showApprove && (
          <ApproveDialog statementId={statementId}
                         onClose={() => setShowApprove(false)}
                         onApproved={() => { setShowApprove(false); load(); onChanged?.(); }} />
        )}
        {showQuery && (
          <QueryDialog statementId={statementId}
                       onClose={() => setShowQuery(false)}
                       onQueried={() => { setShowQuery(false); load(); onChanged?.(); }} />
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldTile({ label, value, bold }) {
  return (
    <div className="st-panel p-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-[13px] tabular-nums truncate ${bold ? "font-semibold text-slate-900" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}


function ApproveDialog({ statementId, onClose, onApproved }) {
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!name.trim()) { toast.error("Signer name is required"); return; }
    setBusy(true);
    try {
      await api.post(`/billing/statements/${statementId}/approve`,
                     { signature_name: name.trim(),
                       signature_title: title.trim() || undefined,
                       remarks: remarks.trim() || undefined });
      toast.success("Statement approved");
      onApproved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approval failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="billing-approve-dialog">
        <DialogHeader><DialogTitle>Approve statement</DialogTitle></DialogHeader>
        <div className="space-y-3 text-[13px]">
          <label>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Signer name</div>
            <Input value={name} onChange={(e) => setName(e.target.value)}
                   data-testid="billing-approve-name" />
          </label>
          <label>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Title (optional)</div>
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
                   data-testid="billing-approve-title" />
          </label>
          <label>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Remarks</div>
            <Input value={remarks} onChange={(e) => setRemarks(e.target.value)}
                   data-testid="billing-approve-remarks" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="billing-approve-submit">
            Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function QueryDialog({ statementId, onClose, onQueried }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (reason.trim().length < 3) { toast.error("Please explain the query"); return; }
    setBusy(true);
    try {
      await api.post(`/billing/statements/${statementId}/query`,
                     { reason: reason.trim() });
      toast.success("Query raised — Suntronix will respond");
      onQueried?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Query failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="billing-query-dialog">
        <DialogHeader><DialogTitle>Raise a query on this statement</DialogTitle></DialogHeader>
        <div className="space-y-3 text-[13px]">
          <label>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Reason</div>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)}
                      rows={5}
                      className="w-full border rounded p-2 text-[13px]"
                      placeholder="Describe what looks incorrect — line items, quantities, rate…"
                      data-testid="billing-query-reason" />
          </label>
          <div className="text-[11px] text-amber-800 flex items-start gap-2">
            <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
            The statement moves to QUERIED and cannot be invoiced until Suntronix
            responds and re-shares it.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="billing-query-submit">
            Raise query
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
